import React, { useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    FilterMultiselect,
    FilterMultiselectOption,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { RulesIcon } from "@icons";
import { ActorType } from "@common/types/auditLog";
import { SQSProcessName } from "@common/constants/process-names";
import { DOCUMENT_SOURCE_INTEGRATIONS } from "@components/history/components/integrationActors";
import { KICK_ACTOR_LABEL, KICK_SUPPORT_LABEL } from "@common/constants/actor";
import { HStack } from "@design-system/HStack/HStack";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { BaseFilterProps } from "../../lib";
import styles from "./ActorFilter.module.scss";
import {
    useConnectedIntegrations,
    hasAllSourceTypes,
} from "./useConnectedIntegrations";

const KICK_VALUE = "__kick__";
const SUPPORT_VALUE = "__support__";
const RULE_VALUE = "__rule__";
const RULE_SOURCE_TYPE = SQSProcessName.TRANSACTION_RULE_APPLICATION;

function integrationValue(label: string): string {
    return `__integration__${label}`;
}

interface ActorFilterProps extends BaseFilterProps {
    integrationsAsDocumentSources?: boolean;
}

export const ActorFilter: React.FC<ActorFilterProps> = ({
    filters,
    onChange,
    integrationsAsDocumentSources,
}) => {
    const { data: members = [] } = useCurrentWorkspaceMembers();
    const allConnectedIntegrations = useConnectedIntegrations();

    const connectedIntegrations = useMemo(
        () =>
            integrationsAsDocumentSources
                ? allConnectedIntegrations.filter((i) =>
                      DOCUMENT_SOURCE_INTEGRATIONS.some(
                          (d) => d.accountSourceType === i.accountSourceType,
                      ),
                  )
                : allConnectedIntegrations,
        [allConnectedIntegrations, integrationsAsDocumentSources],
    );

    const options = useMemo((): FilterMultiselectOption[] => {
        const opts: FilterMultiselectOption[] = [
            {
                value: KICK_VALUE,
                label: (
                    <HStack as="span" align="center" gap={8}>
                        <KickAvatar size="xs" aria-hidden />
                        {KICK_ACTOR_LABEL}
                    </HStack>
                ),
                searchableLabel: KICK_ACTOR_LABEL,
            },
            {
                value: SUPPORT_VALUE,
                label: (
                    <HStack as="span" align="center" gap={8}>
                        <KickAvatar size="xs" aria-hidden />
                        {KICK_SUPPORT_LABEL}
                    </HStack>
                ),
                searchableLabel: KICK_SUPPORT_LABEL,
            },
        ];

        if (!integrationsAsDocumentSources) {
            opts.push({
                value: RULE_VALUE,
                label: (
                    <HStack as="span" align="center" gap={8}>
                        <RulesIcon style={{ width: 16, height: 16 }} />
                        Rules
                    </HStack>
                ),
                searchableLabel: "Rules",
            });
        }

        for (const integration of connectedIntegrations) {
            opts.push({
                value: integrationValue(integration.label),
                label: (
                    <HStack as="span" align="center" gap={8}>
                        <InstitutionIcon
                            institution={integration.institution}
                            size="xs"
                        />
                        {integration.label}
                    </HStack>
                ),
                searchableLabel: integration.label,
            });
        }

        for (const member of members) {
            opts.push({
                value: member.user.id,
                label: (
                    <HStack as="span" align="center" gap={8}>
                        <UserAvatar user={member.user} size="xs" aria-hidden />
                        {member.user.name}
                    </HStack>
                ),
                searchableLabel: `${member.user.name} ${member.user.email ?? ""}`,
            });
        }

        return opts;
    }, [members, connectedIntegrations, integrationsAsDocumentSources]);

    // Derive current value array from filter state
    const value = useMemo(() => {
        const selected: Array<string | number> = [];

        if (filters.actorIds?.includes(null)) {
            selected.push(KICK_VALUE);
        }
        if (filters.actorType === ActorType.SUPPORT) {
            selected.push(SUPPORT_VALUE);
        }
        if (
            !integrationsAsDocumentSources &&
            filters.sourceTypes?.includes(RULE_SOURCE_TYPE)
        ) {
            selected.push(RULE_VALUE);
        }

        for (const integration of connectedIntegrations) {
            let isSelected: boolean;
            if (integrationsAsDocumentSources) {
                isSelected = integration.documentSource
                    ? (filters.sourceTypes?.includes(
                          integration.documentSource,
                      ) ?? false)
                    : false;
            } else {
                isSelected = hasAllSourceTypes(
                    filters.sourceTypes,
                    integration.sourceTypes,
                );
            }
            if (isSelected) {
                selected.push(integrationValue(integration.label));
            }
        }

        for (const id of filters.actorIds ?? []) {
            if (id !== null) {
                selected.push(id);
            }
        }

        return selected;
    }, [
        filters.actorIds,
        filters.actorType,
        filters.sourceTypes,
        connectedIntegrations,
        integrationsAsDocumentSources,
    ]);

    return (
        <div className={styles.filter}>
            <FilterMultiselect
                inDropdown={false}
                options={options}
                value={value}
                onChange={(val) => {
                    const memberIds = val.filter(
                        (v): v is number => typeof v === "number",
                    );
                    const hasKick = val.includes(KICK_VALUE);
                    const hasSupport = val.includes(SUPPORT_VALUE);
                    const hasRule =
                        !integrationsAsDocumentSources &&
                        val.includes(RULE_VALUE);

                    const kickId: null[] = hasKick ? [null] : [];
                    const actorIds: Array<number | null> = [
                        ...memberIds,
                        ...kickId,
                    ];

                    const sourceTypes: string[] = hasRule
                        ? [RULE_SOURCE_TYPE]
                        : [];

                    for (const integration of connectedIntegrations) {
                        if (val.includes(integrationValue(integration.label))) {
                            if (
                                integrationsAsDocumentSources &&
                                integration.documentSource
                            ) {
                                sourceTypes.push(integration.documentSource);
                            } else {
                                sourceTypes.push(...integration.sourceTypes);
                            }
                        }
                    }

                    onChange({
                        actorIds: actorIds.length > 0 ? actorIds : undefined,
                        actorType: hasSupport ? ActorType.SUPPORT : undefined,
                        sourceTypes:
                            sourceTypes.length > 0 ? sourceTypes : undefined,
                    });
                }}
                name="actorIds"
                onBlur={noop}
                searchable
            />
        </div>
    );
};
